export type OrderConfirmationInternalStatus =
  | 'initialized'
  | 'error'
  | 'open'
  | 'validated'
  | 'canceled'
  | 'manualHandling'

export interface ConfirmedQuantity {
  /** Present when the API returns persisted confirmed-quantity ids (required for updates). */
  id?: number
  quantity: number
  /** Supplier-stated delivery date from the OC. */
  delivery_date: string
  /** Calculated arrival date; used for PO sync, display, and deviation checks. */
  arrival_date: string
}

/** Date shown in the UI and compared to the PO requested date (arrival, with delivery fallback). */
export function confirmedQuantityArrivalDate(cq: ConfirmedQuantity): string {
  const arrival = cq.arrival_date?.trim()
  if (arrival) return arrival
  return cq.delivery_date?.trim() ?? ''
}

export interface EmailReference {
  id: string
  subject: string
  sender_address: string
  received: string
  body: string
}

export interface AttachmentReference {
  id: string
  name: string
  bucket: string
  organization_id: string
  workflow_id: string
  process_id: string
}

export interface UserReference {
  id: string
  first_name: string
  last_name: string
  email_address: string
}

export interface OrderConfirmationSource {
  email: EmailReference | null
  attachment: AttachmentReference | null
  created_by: UserReference | null
}

export interface OrderConfirmation {
  id: string
  validated: boolean
  internal_status: OrderConfirmationInternalStatus | string
  received: string
  last_updated: string
  source: OrderConfirmationSource
  /** Who validated this confirmation. Null until it is validated. */
  validated_by: UserReference | null
}

export interface OrderConfirmationHead {
  id: string
  customer_order_number: string
  supplier_order_number: string
  supplier_name: string
  supplier_contact_person: string
  invoice_address_company_name: string
  invoice_address_street: string
  invoice_address_house_number: string
  invoice_address_postal_code: string
  invoice_address_city: string
  invoice_address_country: string
  invoice_address_additional_information: string
  delivery_address_company_name: string
  delivery_address_street: string
  delivery_address_house_number: string
  delivery_address_postal_code: string
  delivery_address_city: string
  delivery_address_country: string
  delivery_address_additional_information: string
  payment_terms: string
  /** What payment_terms was parsed to, the way the backend check parses it. Null when no term is found; absent from an older API. */
  parsed_payment_terms?: number | null
  delivery_terms: string
  delivery_method: string
}

export interface OrderConfirmationRow {
  id: string
  position: string
  supplier_article_number: string
  customer_article_number: string
  article_description: string
  additional_information: string
  unit: string
  unit_price: number | null
  revision_number: string
  drawing_number: string
  row_amount: number
  confirmed_quantities: ConfirmedQuantity[]
}
